// SolmanDocuments.jsx — Mass download dokumen project SAP Solution Manager (SM1/500)
// Backend: backend/solmanDocs.js  (/api/solman-docs/*)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, FolderOpen, RefreshCw, Download, DownloadCloud, Pencil, Check, X,
  AlertTriangle, Loader2, Search, Eye, ChevronRight, ChevronDown, StopCircle, Type, PlayCircle,
} from 'lucide-react';
import SolmanS3Button from './SolmanS3Button';
import SolmanStatusPanel from './SolmanStatusPanel';

// Potongan teks hasil pencarian: <<kata>> → <mark>, tanpa dangerouslySetInnerHTML.
function Snippet({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(<<|>>)/);
  let on = false;
  const out = [];
  parts.forEach((p, i) => {
    if (p === '<<') { on = true; return; }
    if (p === '>>') { on = false; return; }
    out.push(on ? <mark key={i} className="bg-amber-200 text-slate-900 rounded px-0.5">{p}</mark> : <span key={i}>{p}</span>);
  });
  return <>{out}</>;
}

const authHeaders = (json = false) => {
  const h = { Authorization: 'Bearer ' + (localStorage.getItem('auth_token') || '') };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};
async function api(url, opts = {}) {
  const r = await fetch(url, { ...opts, headers: { ...authHeaders(!!opts.body), ...(opts.headers || {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
  return j;
}
async function downloadBlob(url, fallbackName) {
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.error || ('HTTP ' + r.status));
  }
  const cd = r.headers.get('Content-Disposition') || '';
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) || /filename="([^"]+)"/i.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : fallbackName;
  const blob = await r.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
const fmtBytes = (n) => {
  n = Number(n || 0);
  if (n < 1024) return n + ' B';
  if (n < 1048576) return (n / 1024).toFixed(0) + ' KB';
  if (n < 1073741824) return (n / 1048576).toFixed(1) + ' MB';
  return (n / 1073741824).toFixed(2) + ' GB';
};
const fmtSapTs = (s) => {
  s = String(s || '');
  if (!/^\d{14}$/.test(s) || /^0+$/.test(s)) return '';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
};
const shortId = (id) => String(id || '').slice(0, 12) + '…';

// value = label manual (boleh kosong), fallback = nama dari SAP.
// Menyimpan teks yang sama dengan nama SAP (atau kosong) menghapus label manual.
function InlineLabel({ value, fallback, placeholder, onSave }) {
  const shown = value || fallback || '';
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(shown);
  useEffect(() => { setV(shown); }, [shown]);
  const save = () => {
    const t = v.trim();
    onSave(t === (fallback || '').trim() ? '' : t);
    setEdit(false);
  };
  if (!edit) {
    return (
      <span className="inline-flex items-center gap-1 min-w-0">
        <span className={`truncate ${shown ? '' : 'text-slate-400 italic'}`}>{shown || placeholder}</span>
        {value && fallback && value !== fallback && (
          <span className="text-[10px] px-1 rounded bg-amber-50 text-amber-700 border border-amber-200 shrink-0" title={'SAP name: ' + fallback}>edited</span>
        )}
        <button type="button" onClick={(e) => { e.stopPropagation(); setEdit(true); }}
          className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0" title="Edit label">
          <Pencil size={12} />
        </button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 min-w-0" onClick={(e) => e.stopPropagation()}>
      <input autoFocus value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setV(shown); setEdit(false); } }}
        className="px-1.5 py-0.5 text-sm border border-slate-300 rounded w-56" placeholder={fallback || placeholder} />
      <button type="button" onClick={save} className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded"><Check size={14} /></button>
      <button type="button" onClick={() => { setV(shown); setEdit(false); }} className="p-0.5 text-slate-500 hover:bg-slate-100 rounded"><X size={14} /></button>
    </span>
  );
}

export default function SolmanDocuments() {
  const [summary, setSummary] = useState(null);
  // Viewer dokumen di dalam dashboard (tanpa download ke PC). File diambil dengan token
  // (endpoint butuh login) lalu ditampilkan lewat blob URL. ?inline=1 → backend kirim inline;
  // bila lokal sudah dihapus, backend stream langsung dari S3.
  const [viewer, setViewer] = useState(null); // { phio_id, title, url, kind, loading, err }
  const closeViewer = useCallback(() => {
    setViewer((v) => { if (v && v.url) URL.revokeObjectURL(v.url); return null; });
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.has('view')) { u.searchParams.delete('view'); window.history.replaceState(null, '', u.pathname + (u.search || '') + u.hash); }
    } catch (_) {}
  }, []);
  const viewDoc = useCallback(async (d) => {
    setViewer((v) => { if (v && v.url) URL.revokeObjectURL(v.url); return { phio_id: d.phio_id, title: d.descript || d.phio_id, loading: true }; });
    try {
      const r = await fetch('/api/solman-docs/file/' + encodeURIComponent(d.phio_id) + '?inline=1', { headers: authHeaders() });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      const ct = (r.headers.get('Content-Type') || '').toLowerCase();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) || /filename="([^"]+)"/i.exec(cd);
      const name = m ? decodeURIComponent(m[1]) : (d.descript || 'document');
      const ext = (name.split('.').pop() || '').toLowerCase();
      const kind = ct.includes('pdf') || ext === 'pdf' ? 'pdf'
        : (ct.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) ? 'image'
        : (ct.startsWith('text/') || ext === 'txt') ? 'text' : 'other';
      let blob = await r.blob();
      if (kind === 'pdf' && blob.type !== 'application/pdf') blob = new Blob([blob], { type: 'application/pdf' });
      if (kind === 'image' && !blob.type.startsWith('image/')) blob = new Blob([blob], { type: ext === 'png' ? 'image/png' : 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      setViewer((v) => (v && v.phio_id === d.phio_id ? { ...v, title: d.descript || name, name, url, kind, loading: false } : (URL.revokeObjectURL(url), v)));
    } catch (e) {
      setViewer((v) => (v && v.phio_id === d.phio_id ? { ...v, loading: false, err: String(e.message || e) } : v));
    }
  }, []);
  // Deep link: /dashboard/solman-docs?view=<phio_id> (dipakai daftar Referensi jawaban AI).
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('view');
      if (p && /^[0-9A-Fa-f]{32}$/.test(p)) viewDoc({ phio_id: p.toUpperCase(), descript: '' });
    } catch (_) {}
  }, [viewDoc]);
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeViewer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer, closeViewer]);
  const [selected, setSelected] = useState(null);
  const [tree, setTree] = useState(null);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [job, setJob] = useState(null);
  const [ocrJob, setOcrJob] = useState(null);
  const [sq, setSq] = useState('');
  const [sAll, setSAll] = useState(true);
  const [sres, setSres] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [pipe, setPipe] = useState(null);
  const pollRef = useRef(null);
  const ocrPollRef = useRef(null);
  const pipePollRef = useRef(null);

  const loadSummary = useCallback(async () => {
    const j = await api('/api/solman-docs/summary');
    setSummary(j);
    setJob(j.job || null);
    setOcrJob(j.ocrJob || null);
    setPipe(j.pipe || null);
    return j;
  }, []);
  const loadTree = useCallback(async (strucId) => {
    if (!strucId) { setTree(null); return; }
    const j = await api('/api/solman-docs/tree?strucId=' + encodeURIComponent(strucId));
    setTree(j);
  }, []);

  useEffect(() => {
    loadSummary().then((j) => {
      const first = (j.strucs || []).find((x) => !x.hidden) || (j.strucs || [])[0];
      if (!selected && first) setSelected(first.struc_id);
    }).catch((e) => setMsg({ type: 'err', text: e.message }));
  }, []);
  useEffect(() => { loadTree(selected).catch((e) => setMsg({ type: 'err', text: e.message })); }, [selected, loadTree]);

  // Poll job tarik massal.
  useEffect(() => {
    if (job && job.running) {
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const j = await api('/api/solman-docs/job');
            setJob(j.job);
            if (!j.job || !j.job.running) {
              clearInterval(pollRef.current); pollRef.current = null;
              await loadSummary(); await loadTree(selected);
            }
          } catch (_) {}
        }, 2000);
      }
    }
    return () => {};
  }, [job, selected, loadSummary, loadTree]);
  // Poll job OCR (lebih jarang — satu file bisa makan beberapa menit).
  useEffect(() => {
    if (ocrJob && ocrJob.running && !ocrPollRef.current) {
      let lastDone = ocrJob.done;
      ocrPollRef.current = setInterval(async () => {
        try {
          const j = await api('/api/solman-docs/ocr-job');
          setOcrJob(j.ocrJob);
          const done = j.ocrJob ? j.ocrJob.done : 0;
          if (!j.ocrJob || !j.ocrJob.running) {
            clearInterval(ocrPollRef.current); ocrPollRef.current = null;
            await loadSummary(); await loadTree(selected);
          } else if (done !== lastDone) {
            lastDone = done;
            await loadSummary(); await loadTree(selected);
          }
        } catch (_) {}
      }, 5000);
    }
    return () => {};
  }, [ocrJob, selected, loadSummary, loadTree]);
  // Poll pipeline (Sync → Fetch → OCR → S3 → Housekeeping). Job fetch/OCR di dalamnya ikut di-set
  // supaya panel progress fetch/OCR yang sudah ada tetap tampil.
  useEffect(() => {
    if (pipe && pipe.running && !pipePollRef.current) {
      let lastKey = '';
      pipePollRef.current = setInterval(async () => {
        try {
          const j = await api('/api/solman-docs/pipeline');
          setPipe(j.pipe);
          if (j.job) setJob(j.job);
          if (j.ocrJob) setOcrJob(j.ocrJob);
          const p = j.pipe;
          const key = p ? (p.step || '') + ':' + p.steps.map((s) => s.status).join(',') : '';
          if (!p || !p.running) {
            clearInterval(pipePollRef.current); pipePollRef.current = null;
            await loadSummary(); await loadTree(selected);
          } else if (key !== lastKey) {
            lastKey = key;
            await loadSummary(); await loadTree(selected);
          }
        } catch (_) {}
      }, 3000);
    }
    return () => {};
  }, [pipe, selected, loadSummary, loadTree]);
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (ocrPollRef.current) clearInterval(ocrPollRef.current);
    if (pipePollRef.current) clearInterval(pipePollRef.current);
  }, []);

  const run = async (label, fn) => {
    setBusy(label); setMsg(null);
    try { await fn(); } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setBusy(''); }
  };

  const syncList = (strucId) => run('sync', async () => {
    const r = await api('/api/solman-docs/sync-list', { method: 'POST', body: JSON.stringify({ strucId: strucId || '' }) });
    setMsg(r.namesError
      ? { type: 'err', text: `List synced (${r.rows} document versions), but reading names from SAP failed: ${r.namesError}` }
      : { type: 'ok', text: `Synced from SAP: ${r.rows} document versions` + (r.names ? `, ${r.names.nodes} roadmap nodes` : '') + ` (${(r.ms / 1000).toFixed(1)} s).` });
    const s = await loadSummary();
    if (!selected && s.strucs && s.strucs.length) setSelected(s.strucs[0].struc_id);
    else await loadTree(selected);
  });
  const startFetch = (body, label) => run('fetch', async () => {
    const r = await api('/api/solman-docs/fetch', { method: 'POST', body: JSON.stringify(body) });
    setMsg({ type: 'ok', text: `${label}: ${r.queued} documents queued (≈ ${fmtBytes(r.estBytes)}).` });
    const j = await api('/api/solman-docs/job');
    setJob(j.job);
  });
  const cancelJob = () => run('cancel', async () => {
    await api('/api/solman-docs/job/cancel', { method: 'POST', body: '{}' });
  });
  const startOcr = (body, label) => run('ocr', async () => {
    const r = await api('/api/solman-docs/ocr', { method: 'POST', body: JSON.stringify(body) });
    setMsg({ type: 'ok', text: `${label}: ${r.queued} documents queued for OCR. This runs in the background (about 5–9 s per page).` });
    const j = await api('/api/solman-docs/ocr-job');
    setOcrJob(j.ocrJob);
  });
  const cancelOcr = () => run('cancelocr', async () => {
    await api('/api/solman-docs/ocr-job/cancel', { method: 'POST', body: '{}' });
  });
  const startPipeline = (s) => {
    if (!window.confirm(`Run pipeline for "${s.name || s.struc_id}"?\n\n` +
      'Sync list → then in batches: Fetch PDFs → OCR → Upload to S3.\n\n' +
      'Each PDF is deleted from the server disk right after its S3 upload is verified (MD5). ' +
      'Documents are then opened directly from S3.')) return;
    run('pipeline', async () => {
      const r = await api('/api/solman-docs/pipeline', { method: 'POST', body: JSON.stringify({ strucId: s.struc_id }) });
      setPipe(r.pipe);
      setMsg({ type: 'ok', text: `Pipeline started for ${s.name || 'project'}. It runs in the background; you can leave this page.` });
    });
  };
  const cancelPipeline = () => run('cancelpipe', async () => {
    const r = await api('/api/solman-docs/pipeline/cancel', { method: 'POST', body: '{}' });
    setPipe(r.pipe);
  });
  const setHidden = (s, hidden) => run('hide', async () => {
    await api('/api/solman-docs/hide', { method: 'POST', body: JSON.stringify({
      strucId: s.struc_id, hidden, reason: hidden ? 'Duplicate / old copy' : '' }) });
    const j = await loadSummary();
    if (hidden && !showHidden) {
      const next = (j.strucs || []).find((x) => !x.hidden);
      if (next) setSelected(next.struc_id);
    }
  });
  const doSearch = () => run('search', async () => {
    const t = sq.trim();
    if (!t) { setSres(null); return; }
    const qs = 'q=' + encodeURIComponent(t) + (!sAll && selected ? '&strucId=' + encodeURIComponent(selected) : '');
    setSres(await api('/api/solman-docs/search?' + qs));
  });
  const saveLabel = (kind, id, label) => run('label', async () => {
    await api('/api/solman-docs/label', { method: 'POST', body: JSON.stringify({ kind, id, label }) });
    await loadSummary(); await loadTree(selected);
  });
  const dlFile = (d) => run('dl:' + d.phio_id, async () => {
    await downloadBlob('/api/solman-docs/file/' + d.phio_id, (d.descript || 'document') + '.pdf');
    if (!d.has_file) { await loadTree(selected); await loadSummary(); }
  });

  const strucs = (summary && summary.strucs) || [];
  const hiddenCount = strucs.filter((s) => s.hidden).length;
  const listStrucs = showHidden ? strucs : strucs.filter((s) => !s.hidden || s.struc_id === selected);
  const cur = strucs.find((s) => s.struc_id === selected);
  const nodes = useMemo(() => {
    const list = (tree && tree.nodes) || [];
    const t = q.trim().toLowerCase();
    if (!t) return list;
    return list.map((n) => {
      const hay = [n.label, n.sap_name, n.num, n.path].filter(Boolean).join(' ').toLowerCase();
      return { ...n, docs: hay.includes(t) ? n.docs : n.docs.filter((d) => (d.descript || '').toLowerCase().includes(t)) };
    }).filter((n) => n.docs.length);
  }, [tree, q]);
  const disk = summary && summary.disk;
  const jobRunning = job && job.running;
  const pct = job && job.total ? Math.round((job.done / job.total) * 100) : 0;
  const ocrRunning = ocrJob && ocrJob.running;
  const ocrPct = ocrJob && ocrJob.total ? Math.round((ocrJob.done / ocrJob.total) * 100) : 0;
  const pipeRunning = !!(pipe && pipe.running);
  const STEP_STYLE = {
    pending: 'border-slate-200 text-slate-400 bg-white',
    running: 'border-indigo-300 text-indigo-700 bg-indigo-50',
    done: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    warn: 'border-amber-300 text-amber-700 bg-amber-50',
    skipped: 'border-slate-200 text-slate-400 bg-slate-50',
    error: 'border-rose-300 text-rose-700 bg-rose-50',
    cancelled: 'border-rose-200 text-rose-600 bg-rose-50',
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 text-white" style={{ background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>Solman Documents</h1>
          <p className="text-xs text-white/80 mt-1">
            Project documents from SAP Solution Manager SM1/500 (RMMAIN / SOLAR) via ZWS_SOLMAN_DOCS.
            {summary && <> Stored in <code className="text-xs bg-white/20 px-1 rounded">{summary.baseDir}</code>.</>}
          </p>
          {disk && (
            <p className={`text-xs mt-1 ${disk.free < disk.reserve * 2 ? 'text-rose-100 font-semibold' : 'text-white/70'}`}>
              Disk: {fmtBytes(disk.free)} free of {fmtBytes(disk.total)} · at least {fmtBytes(disk.reserve)} is always kept free (fetch is refused below that)
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!!busy || jobRunning || pipeRunning} onClick={() => syncList('')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-white/15 ring-1 ring-inset ring-white/30 text-white hover:bg-white/25 disabled:opacity-50">
            {busy === 'sync' ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Sync list (all projects)
          </button>
          <button type="button" disabled={!!busy || jobRunning || pipeRunning || !strucs.length}
            onClick={() => startFetch({}, 'All projects')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-white text-brand-700 hover:bg-white/90 disabled:opacity-50 shadow-sm">
            <DownloadCloud size={15} /> Fetch all PDFs
          </button>
        </div>
      </div>

      {msg && (
        <div className={`text-sm px-3 py-2 rounded-lg border ${msg.type === 'err' ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
          {msg.type === 'err' && <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />}{msg.text}
        </div>
      )}

      {/* Pencarian isi dokumen */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); doSearch(); }}>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={sq} onChange={(e) => setSq(e.target.value)}
              placeholder="Search inside documents (OCR text) and titles, e.g. IMB gudang, 5600 M2, AIG"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg" />
          </div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1">
            <input type="checkbox" checked={sAll} onChange={(e) => setSAll(e.target.checked)} /> All projects
          </label>
          <button type="submit" disabled={busy === 'search'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
            {busy === 'search' ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
          </button>
          {sres && <button type="button" onClick={() => { setSres(null); setSq(''); }} className="text-xs text-slate-500 hover:underline">Clear</button>}
        </form>
        {sres && (
          <div className="mt-3">
            <div className="text-xs text-slate-500 mb-1">
              {sres.results.length} result(s){sres.results.length >= 50 ? ' (showing first 50)' : ''}. Only documents that have been OCR'd are searchable by content; others match by title only.
            </div>
            <ul className="divide-y divide-slate-100 max-h-[45vh] overflow-auto">
              {sres.results.map((r) => (
                <li key={r.phio_id} className="py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{r.title}</div>
                    <div className="text-xs text-slate-500 truncate">{[r.struc_name, r.node_name].filter(Boolean).join(' › ')}</div>
                    {r.snip
                      ? <div className="text-xs text-slate-600 mt-0.5"><Snippet text={r.snip} /></div>
                      : <div className="text-xs text-slate-400 mt-0.5 italic">title match (content not OCR'd yet)</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => { setSelected(r.struc_id); setOpen((o) => ({ ...o, [r.node_id]: true })); }}
                      className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">Show</button>
                    <button type="button" onClick={() => viewDoc({ phio_id: r.phio_id, descript: r.title })}
                      title="View" className="p-1 rounded hover:bg-slate-100 text-slate-600"><Eye size={14} /></button>
                    <button type="button" onClick={() => dlFile({ phio_id: r.phio_id, descript: r.title, has_file: r.has_file })}
                      title="Download" className="p-1 rounded hover:bg-slate-100 text-slate-600"><Download size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {pipe && (
        <div className="rounded-xl border border-indigo-200 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              {pipeRunning ? <Loader2 size={15} className="animate-spin text-indigo-600 shrink-0" />
                : pipe.status === 'done' ? <Check size={15} className="text-emerald-600 shrink-0" />
                  : <AlertTriangle size={15} className={`shrink-0 ${pipe.status === 'warn' ? 'text-amber-600' : 'text-rose-600'}`} />}
              <span className="font-medium text-slate-700 shrink-0">{pipeRunning ? 'Pipeline running' : 'Last pipeline'}</span>
              <span className="text-slate-500 truncate">{pipe.name}{pipe.batch ? ` · batch ${pipe.batch} (${pipe.batchSize}/batch)` : ''}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
              <span>{pipe.username} · {String(pipe.started || '').replace('T', ' ').slice(0, 16)}</span>
              {pipeRunning && (
                <button type="button" onClick={cancelPipeline} disabled={busy === 'cancelpipe' || pipe.cancel}
                  className="inline-flex items-center gap-1 text-sm text-rose-600 hover:underline disabled:opacity-50">
                  <StopCircle size={14} /> {pipe.cancel ? 'Stopping…' : 'Stop'}
                </button>
              )}
            </div>
          </div>
          <ol className="mt-2 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
            {pipe.steps.map((s, i) => (
              <li key={s.key} className={`rounded-lg border px-2 py-1.5 text-xs ${STEP_STYLE[s.status] || STEP_STYLE.pending}`}>
                <div className="flex items-center gap-1 font-medium">
                  {s.status === 'running' ? <Loader2 size={12} className="animate-spin" />
                    : s.status === 'done' ? <Check size={12} />
                      : (s.status === 'error' || s.status === 'cancelled') ? <X size={12} />
                        : s.status === 'warn' ? <AlertTriangle size={12} /> : <span className="w-3 text-center">{i + 1}</span>}
                  {s.label}
                </div>
                {s.detail && <div className="mt-0.5 opacity-80 break-words">{s.detail}</div>}
              </li>
            ))}
          </ol>
          {!pipeRunning && pipe.error && <div className="mt-2 text-xs text-rose-700">{pipe.error}</div>}
        </div>
      )}

      {ocrJob && (ocrJob.running || ocrJob.finished) && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              {ocrRunning ? <Loader2 size={15} className="animate-spin text-emerald-600 shrink-0" /> : <Check size={15} className="text-emerald-600 shrink-0" />}
              <span className="font-medium text-slate-700 shrink-0">{ocrRunning ? 'OCR running' : 'Last OCR'}</span>
              <span className="text-slate-500 truncate">{ocrRunning ? (ocrJob.current || '') : ''}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0 text-slate-600">
              <span>{ocrJob.done}/{ocrJob.total}</span>
              <span className="text-emerald-700">{ocrJob.ok} ok</span>
              {ocrJob.failed > 0 && <span className="text-rose-600">{ocrJob.failed} failed</span>}
              <span>{ocrJob.pages} pages</span>
              {ocrRunning && (
                <button type="button" onClick={cancelOcr} className="inline-flex items-center gap-1 text-rose-600 hover:underline">
                  <StopCircle size={14} /> Stop
                </button>
              )}
            </div>
          </div>
          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: ocrPct + '%' }} />
          </div>
          {!ocrRunning && ocrJob.stopReason && <div className="mt-2 text-xs text-rose-700">{ocrJob.stopReason}</div>}
          {!ocrRunning && ocrJob.errors && ocrJob.errors.length > 0 && (
            <details className="mt-2 text-xs text-rose-700">
              <summary className="cursor-pointer">{ocrJob.errors.length} error(s)</summary>
              <ul className="mt-1 space-y-0.5">
                {ocrJob.errors.map((e, i) => <li key={i}>{e.title || e.phio}: {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {job && (job.running || job.finished) && (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              {jobRunning ? <Loader2 size={15} className="animate-spin text-indigo-600 shrink-0" /> : <Check size={15} className="text-emerald-600 shrink-0" />}
              <span className="font-medium text-slate-700 shrink-0">{jobRunning ? 'Fetching' : 'Last fetch'}</span>
              <span className="text-slate-500 truncate">{jobRunning ? (job.current || '') : (job.scope || '')}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0 text-slate-600">
              <span>{job.done}/{job.total}</span>
              <span className="text-emerald-700">{job.ok} ok</span>
              {job.failed > 0 && <span className="text-rose-600">{job.failed} failed</span>}
              <span>{fmtBytes(job.bytes)}</span>
              {jobRunning && (
                <button type="button" onClick={cancelJob} className="inline-flex items-center gap-1 text-rose-600 hover:underline">
                  <StopCircle size={14} /> Stop
                </button>
              )}
            </div>
          </div>
          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: pct + '%' }} />
          </div>
          {!jobRunning && job.stopReason && <div className="mt-2 text-xs text-rose-700">{job.stopReason}</div>}
          {!jobRunning && job.errors && job.errors.length > 0 && (
            <details className="mt-2 text-xs text-rose-700">
              <summary className="cursor-pointer">{job.errors.length} error(s)</summary>
              <ul className="mt-1 space-y-0.5">
                {job.errors.map((e, i) => <li key={i}>{e.title || e.phio}: {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      <SolmanStatusPanel mode="overview" selected={selected} onSelect={setSelected} />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Daftar project */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden self-start">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Projects / roadmaps ({strucs.length - hiddenCount})
            </span>
            {hiddenCount > 0 && (
              <label className="text-xs text-slate-500 inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
                Show hidden ({hiddenCount})
              </label>
            )}
          </div>
          {!strucs.length && (
            <div className="p-4 text-sm text-slate-500">No data yet. Click <b>Sync list</b> to read the document list from SAP.</div>
          )}
          <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-auto">
            {listStrucs.map((s) => (
              <li key={s.struc_id}>
                <div role="button" tabIndex={0} onClick={() => setSelected(s.struc_id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSelected(s.struc_id); }}
                  className={`w-full text-left px-3 py-2 cursor-pointer ${selected === s.struc_id ? 'bg-indigo-50' : 'hover:bg-slate-50'} ${s.hidden ? 'opacity-60' : ''}`}>
                  <div className="text-sm font-medium text-slate-800">
                    <InlineLabel value={s.label} fallback={s.auto_label} placeholder={shortId(s.struc_id)} onSave={(v) => saveLabel('struc', s.struc_id, v)} />
                  </div>
                  {s.hidden ? (
                    <div className="text-[11px] text-amber-700">Hidden{s.hidden_reason ? ' — ' + s.hidden_reason : ''}</div>
                  ) : null}
                  {s.roadmap_text && <div className="text-xs text-slate-600 truncate">Roadmap: {s.roadmap_text}</div>}
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                    <span>{s.nodes} groups</span>
                    <span>{s.docs} docs</span>
                    <span className={s.fetched === s.docs && s.docs ? 'text-emerald-700' : ''}>{s.fetched} fetched</span>
                    {s.failed > 0 && <span className="text-rose-600">{s.failed} failed</span>}
                    {s.bytes > 0 && <span>{fmtBytes(s.bytes)}</span>}
                    {s.ocr_done > 0 && <span className="text-emerald-700">{s.ocr_done} searchable</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Isi project */}
        <div className="rounded-xl border border-slate-200 bg-white min-w-0">
          {!cur ? (
            <div className="p-6 text-sm text-slate-500">Select a project.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">
                    {cur.name || cur.struc_id}
                    {cur.hidden ? <span className="ml-2 text-[11px] font-normal px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">hidden{cur.hidden_reason ? ': ' + cur.hidden_reason : ''}</span> : null}
                  </div>
                  <div className="text-xs text-slate-500">
                    {cur.roadmap_text && <>Roadmap <b className="font-medium text-slate-600">{cur.roadmap_text}</b> · </>}
                    {cur.project_id && <>Project {cur.project_id} · </>}
                    {cur.created_on && <>structure created {cur.created_on} · </>}
                    <span className="font-mono">{cur.struc_id}</span>
                  </div>
                  {cur.fetched > 0 && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      Searchable (OCR): <b className={cur.ocr_done === cur.fetched ? 'text-emerald-700' : 'text-slate-700'}>{cur.ocr_done}/{cur.fetched}</b>
                      {cur.ocr_failed > 0 && <span className="text-rose-600"> · {cur.ocr_failed} OCR failed</span>}
                    </div>
                  )}
                  {cur.docs > cur.fetched && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {cur.docs - cur.fetched} not fetched yet ≈ {fmtBytes(cur.est_bytes)}
                      {disk && cur.est_bytes > disk.free - disk.reserve && <span className="text-rose-600"> — exceeds free disk</span>}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={!!busy || jobRunning || ocrRunning || pipeRunning}
                    onClick={() => startPipeline(cur)}
                    title="Sync list → in batches: Fetch PDFs → OCR → Upload S3 (local PDF deleted after verified upload) → cleanup"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
                    {busy === 'pipeline' || (pipeRunning && pipe.strucId === cur.struc_id)
                      ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Run pipeline
                  </button>
                  <button type="button" disabled={!!busy || jobRunning || pipeRunning} onClick={() => syncList(cur.struc_id)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
                    <RefreshCw size={14} /> Sync
                  </button>
                  <button type="button" disabled={!!busy || jobRunning || pipeRunning} onClick={() => startFetch({ strucId: cur.struc_id }, cur.name || 'Project')}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                    <DownloadCloud size={14} /> Fetch PDFs
                  </button>
                  <button type="button" disabled={!!busy || ocrRunning || pipeRunning || !cur.fetched || cur.ocr_done >= cur.fetched}
                    onClick={() => startOcr({ strucId: cur.struc_id, retryErrors: true }, cur.name || 'Project')}
                    title="Make the fetched PDFs searchable (OCR). Runs in the background."
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                    {busy === 'ocr' ? <Loader2 size={14} className="animate-spin" /> : <Type size={14} />} OCR
                  </button>
                  <SolmanS3Button strucId={cur.struc_id} />
                  <button type="button" disabled={!!busy} onClick={() => setHidden(cur, !cur.hidden)}
                    title={cur.hidden ? 'Show this structure in the list again' : 'Hide this structure (e.g. duplicate copy) from the list, fetch-all and search'}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    {cur.hidden ? 'Unhide' : 'Hide'}
                  </button>
                </div>
              </div>
              <SolmanStatusPanel mode="detail" strucId={cur.struc_id} />
              <div className="px-4 py-2 border-b border-slate-100">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search document title or group label"
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg" />
                </div>
              </div>
              <div className="divide-y divide-slate-100 max-h-[70vh] overflow-auto">
                {nodes.map((n) => {
                  const isOpen = q ? true : !!open[n.node_id];
                  const fetched = n.docs.filter((d) => d.has_file).length;
                  return (
                    <div key={n.node_id}>
                      <div className="px-4 py-2 flex items-center justify-between gap-2 hover:bg-slate-50">
                        <button type="button" onClick={() => setOpen((o) => ({ ...o, [n.node_id]: !o[n.node_id] }))}
                          className="flex items-center gap-1.5 min-w-0 text-left">
                          {isOpen ? <ChevronDown size={15} className="text-slate-400 shrink-0" /> : <ChevronRight size={15} className="text-slate-400 shrink-0" />}
                          {n.num && <span className="text-xs font-mono text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 shrink-0">{n.num}</span>}
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-slate-700">
                              <InlineLabel value={n.label} fallback={n.sap_name}
                                placeholder={n.deleted ? 'Group deleted in SAP roadmap' : 'Group ' + shortId(n.node_id)}
                                onSave={(v) => saveLabel('node', n.node_id, v)} />
                            </span>
                            {n.path && <span className="block text-xs text-slate-400 truncate">{n.path}</span>}
                          </span>
                        </button>
                        <div className="flex items-center gap-2 shrink-0 text-xs text-slate-500">
                          <span>{fetched}/{n.docs.length}</span>
                          <button type="button" title="Fetch PDFs in this group" disabled={!!busy || jobRunning || pipeRunning}
                            onClick={() => startFetch({ strucId: cur.struc_id, nodeId: n.node_id }, n.label || n.sap_name || 'Group')}
                            className="p-1 rounded hover:bg-indigo-50 text-indigo-600 disabled:opacity-40"><DownloadCloud size={15} /></button>
                        </div>
                      </div>
                      {isOpen && (
                        <ul className="pb-2">
                          {n.docs.map((d) => (
                            <li key={d.phio_id} className="pl-10 pr-4 py-1.5 flex items-center justify-between gap-3 text-sm hover:bg-slate-50">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText size={14} className={d.has_file ? 'text-rose-500 shrink-0' : 'text-slate-300 shrink-0'} />
                                <span className="truncate text-slate-700" title={d.descript}>{d.descript}</span>
                              </div>
                              <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
                                {d.fetch_error && <span className="text-rose-600" title={d.fetch_error}><AlertTriangle size={13} className="inline" /> error</span>}
                                {d.ocr_status === 'done' && <span className="text-[10px] px-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" title={d.ocr_pages ? d.ocr_pages + ' pages OCR\'d' : 'OCR done'}>OCR</span>}
                                {d.ocr_status === 'error' && <span className="text-rose-600" title={d.ocr_error || ''}><AlertTriangle size={13} className="inline" /> OCR</span>}
                                <span>{d.doc_format}</span>
                                <span>{fmtSapTs(d.chng_time) || fmtSapTs(d.crea_time)}</span>
                                {d.file_size ? <span>{fmtBytes(d.file_size)}</span> : null}
                                <button type="button" onClick={() => viewDoc(d)} title="View"
                                  className="p-1 rounded hover:bg-slate-100 text-slate-600"><Eye size={14} /></button>
                                <button type="button" onClick={() => dlFile(d)} disabled={busy === 'dl:' + d.phio_id}
                                  title={d.has_file ? 'Download' : 'Fetch from SAP and download'}
                                  className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40">
                                  {busy === 'dl:' + d.phio_id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {tree && !nodes.length && <div className="p-6 text-sm text-slate-500">No documents.</div>}
              </div>
            </>
          )}
        </div>
      </div>
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-6" onClick={closeViewer}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200">
              <FileText size={16} className="text-slate-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate" title={viewer.title}>{viewer.title || 'Document'}</div>
                <div className="text-[11px] text-slate-400 font-mono">{viewer.phio_id}</div>
              </div>
              <button type="button" onClick={() => dlFile({ phio_id: viewer.phio_id, descript: viewer.title, has_file: true })}
                title="Download" className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><Download size={16} /></button>
              <button type="button" onClick={closeViewer} title="Close (Esc)" className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><X size={16} /></button>
            </div>
            <div className="flex-1 min-h-0 bg-slate-100">
              {viewer.loading && <div className="h-full flex items-center justify-center text-slate-500 text-sm gap-2"><Loader2 size={16} className="animate-spin" /> Loading document…</div>}
              {viewer.err && <div className="h-full flex items-center justify-center text-rose-600 text-sm gap-2 p-4 text-center"><AlertTriangle size={16} /> {viewer.err}</div>}
              {!viewer.loading && !viewer.err && viewer.kind === 'pdf' && <iframe title={viewer.title} src={viewer.url} className="w-full h-full border-0 bg-white" />}
              {!viewer.loading && !viewer.err && viewer.kind === 'image' && <div className="h-full overflow-auto flex items-start justify-center p-3"><img src={viewer.url} alt={viewer.title} className="max-w-full h-auto shadow" /></div>}
              {!viewer.loading && !viewer.err && viewer.kind === 'text' && <iframe title={viewer.title} src={viewer.url} className="w-full h-full border-0 bg-white" />}
              {!viewer.loading && !viewer.err && viewer.kind === 'other' && (
                <div className="h-full flex flex-col items-center justify-center text-slate-600 text-sm gap-3 p-4 text-center">
                  <div>This file type ({viewer.name || 'unknown'}) can't be previewed in the browser.</div>
                  <button type="button" onClick={() => dlFile({ phio_id: viewer.phio_id, descript: viewer.title, has_file: true })}
                    className="px-3 py-1.5 rounded border border-slate-300 hover:bg-white inline-flex items-center gap-1"><Download size={14} /> Download</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
